import Fuse, { type FuseOptionKey, type FuseResult } from 'fuse.js';
import { useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';

interface UseFuseSearchOptions<T extends Record<string, unknown>, R = T> {
  data: T[];
  keys: FuseOptionKey<T>[];
  initialReturnAll?: boolean;
  keyword: string | null | undefined;
  mapResultItem?: (result: FuseResult<T>) => R;
}

export default function useFuseSearch<T extends Record<string, unknown>, R = T>({
  data,
  keys,
  initialReturnAll = false,
  keyword,
  mapResultItem = ({ item }): R => item as unknown as R,
}: UseFuseSearchOptions<T, R>) {
  const fuseRef = useRef<Fuse<T>>(
    new Fuse(data, {
      includeScore: true,
      includeMatches: true,
      threshold: 0.3,
      shouldSort: true,
      keys,
    }),
  );
  const [debouncedKeyword] = useDebounce(keyword ?? '', 300);

  useEffect(() => {
    fuseRef.current.setCollection(data);
  }, [data]);

  if (!debouncedKeyword) {
    if (initialReturnAll) {
      return data.map((r) => mapResultItem({ item: r } as FuseResult<T>));
    }

    return [];
  }

  return fuseRef.current.search(debouncedKeyword).map(mapResultItem);
}
